import { useState, useEffect, useCallback, useRef } from 'react';
import { getRoute, type RouteResponse, type RouteStep, type CostingModel, type LatLng } from '@/api/geo';
import { decodePolyline } from '../lib/polyline';
import { findNearestPointOnRoute } from '../lib/distance';
import { useGpsTracking, type GpsPosition } from './useGpsTracking';

// Constants
const ADVANCE_THRESHOLD = 30; // meters - switch to next instruction
const OFF_ROUTE_THRESHOLD = 50; // meters - show "off route" warning

export interface NavigationState {
  // Route data
  route: RouteResponse | null;
  decodedShape: [number, number][]; // [lon, lat] pairs
  maneuvers: RouteStep[];

  // Progress
  currentManeuverIndex: number;
  distanceToNextManeuver: number; // meters
  remainingDistance: number; // meters
  remainingTime: number; // seconds

  // User position
  userPosition: GpsPosition | null;
  isOffRoute: boolean;

  // Start point (for manual/dragged start)
  startPoint: LatLng | null;

  // Current mode
  costing: CostingModel;

  // UI state
  isLoading: boolean;
  isRerouting: boolean;
  error: string | null;
  followUser: boolean;
}

export interface UseNavigationParams {
  destination: LatLng;
  costing: CostingModel;
  autoStart?: boolean;
  manualStartPoint?: LatLng | null;
}

export interface UseNavigationResult {
  state: NavigationState;
  actions: {
    start: () => void;
    stop: () => void;
    reroute: () => Promise<void>;
    rerouteFromPoint: (point: LatLng) => Promise<void>;
    setCosting: (costing: CostingModel) => void;
    toggleFollow: () => void;
  };
}

export function useNavigation({
  destination,
  costing: initialCosting,
  autoStart = true,
  manualStartPoint = null,
}: UseNavigationParams): UseNavigationResult {
  const [state, setState] = useState<NavigationState>({
    route: null,
    decodedShape: [],
    maneuvers: [],
    currentManeuverIndex: 0,
    distanceToNextManeuver: 0,
    remainingDistance: 0,
    remainingTime: 0,
    userPosition: null,
    isOffRoute: false,
    startPoint: manualStartPoint,
    costing: initialCosting,
    isLoading: true,
    isRerouting: false,
    error: null,
    followUser: true,
  });

  const gps = useGpsTracking();

  // Use refs to avoid dependency issues
  const isActiveRef = useRef(false);
  const destinationRef = useRef(destination);
  const routeFetchedRef = useRef(false);
  const gpsRef = useRef(gps);
  const stateRef = useRef(state);

  // Update refs when values change
  gpsRef.current = gps;
  destinationRef.current = destination;
  stateRef.current = state;

  // Fetch route from a point to destination
  const fetchRoute = useCallback(async (fromPoint: LatLng, costingOverride?: CostingModel) => {
    const costingToUse = costingOverride ?? stateRef.current.costing;

    try {
      const route = await getRoute(
        { lat: fromPoint.lat, lon: fromPoint.lon },
        destinationRef.current,
        costingToUse
      );

      // Decode first leg's shape
      const firstLeg = route.trip.legs[0];
      const decodedShape = decodePolyline(firstLeg.shape);

      setState((prev) => ({
        ...prev,
        route,
        decodedShape,
        maneuvers: firstLeg.maneuvers,
        currentManeuverIndex: 0,
        remainingDistance: route.trip.summary.length * 1000, // km to meters
        remainingTime: route.trip.summary.time,
        startPoint: fromPoint,
        costing: costingToUse,
        isLoading: false,
        isRerouting: false,
        error: null,
      }));
    } catch (err) {
      console.error('Failed to fetch route:', err);
      setState((prev) => ({
        ...prev,
        error: 'Не удалось построить маршрут',
        isLoading: false,
        isRerouting: false,
      }));
    }
  }, []);

  // Start navigation
  const start = useCallback(() => {
    if (isActiveRef.current) return;
    isActiveRef.current = true;
    routeFetchedRef.current = false;

    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    gpsRef.current.start();
  }, []);

  // Stop navigation
  const stop = useCallback(() => {
    isActiveRef.current = false;
    gpsRef.current.stop();
  }, []);

  // Reroute from current GPS position or last start point
  const reroute = useCallback(async () => {
    const position = gpsRef.current.position;
    const startPoint = stateRef.current.startPoint;

    // Use GPS position if available, otherwise use last start point
    const fromPoint = position
      ? { lat: position.lat, lon: position.lon }
      : startPoint;

    if (!fromPoint) return;

    setState((prev) => ({ ...prev, isRerouting: true }));
    await fetchRoute(fromPoint);
  }, [fetchRoute]);

  // Reroute from a specific point (e.g., after dragging start marker)
  const rerouteFromPoint = useCallback(async (point: LatLng) => {
    setState((prev) => ({ ...prev, isRerouting: true, followUser: false }));
    await fetchRoute(point);
  }, [fetchRoute]);

  // Change costing mode and reroute
  const setCosting = useCallback((newCosting: CostingModel) => {
    const position = gpsRef.current.position;
    const startPoint = stateRef.current.startPoint;

    const fromPoint = position
      ? { lat: position.lat, lon: position.lon }
      : startPoint;

    if (!fromPoint) {
      // Just update costing in state if no route yet
      setState((prev) => ({ ...prev, costing: newCosting }));
      return;
    }

    setState((prev) => ({ ...prev, isRerouting: true, costing: newCosting }));
    fetchRoute(fromPoint, newCosting);
  }, [fetchRoute]);

  // Toggle follow user mode
  const toggleFollow = useCallback(() => {
    setState((prev) => ({ ...prev, followUser: !prev.followUser }));
  }, []);

  // Handle manual start point - fetch route immediately when set
  useEffect(() => {
    if (manualStartPoint && !routeFetchedRef.current) {
      routeFetchedRef.current = true;
      setState((prev) => ({ ...prev, isLoading: true, error: null }));
      fetchRoute(manualStartPoint);
    }
  }, [manualStartPoint, fetchRoute]);

  // Handle initial route fetch when GPS position is available
  useEffect(() => {
    // Skip if we have a manual start point
    if (manualStartPoint) return;

    if (gps.position && state.isLoading && !routeFetchedRef.current && !state.error) {
      routeFetchedRef.current = true;
      fetchRoute({ lat: gps.position.lat, lon: gps.position.lon });
    }
  }, [gps.position, state.isLoading, state.error, fetchRoute, manualStartPoint]);

  // Update user position and navigation progress
  useEffect(() => {
    if (!gps.position || !state.route || state.decodedShape.length === 0) {
      return;
    }

    const userPos = gps.position;

    // Find nearest point on route
    const nearest = findNearestPointOnRoute(userPos.lat, userPos.lon, state.decodedShape);
    const isOffRoute = nearest.distance > OFF_ROUTE_THRESHOLD;

    // Calculate distance to next maneuver
    const currentManeuver = state.maneuvers[state.currentManeuverIndex];
    let distanceToNextManeuver = 0;
    let newManeuverIndex = state.currentManeuverIndex;

    if (currentManeuver) {
      distanceToNextManeuver = currentManeuver.distance;

      // Check if we should advance to next maneuver
      if (distanceToNextManeuver < ADVANCE_THRESHOLD && newManeuverIndex < state.maneuvers.length - 1) {
        newManeuverIndex = state.currentManeuverIndex + 1;
      }
    }

    // Recalculate remaining distance and time based on progress
    const totalPoints = state.decodedShape.length;
    const progressRatio = Math.min(nearest.index / totalPoints, 1);
    const originalDistance = state.route.trip.summary.length * 1000;
    const originalTime = state.route.trip.summary.time;
    const remainingDistance = originalDistance * (1 - progressRatio);
    const remainingTime = originalTime * (1 - progressRatio);

    // Only update if values changed to prevent unnecessary re-renders
    setState((prev) => {
      if (
        prev.userPosition?.lat === userPos.lat &&
        prev.userPosition?.lon === userPos.lon &&
        prev.isOffRoute === isOffRoute &&
        prev.currentManeuverIndex === newManeuverIndex
      ) {
        return prev;
      }
      return {
        ...prev,
        userPosition: userPos,
        isOffRoute,
        currentManeuverIndex: newManeuverIndex,
        distanceToNextManeuver,
        remainingDistance,
        remainingTime,
      };
    });
  }, [gps.position, state.route, state.decodedShape, state.maneuvers.length, state.currentManeuverIndex]);

  // Handle GPS errors
  useEffect(() => {
    if (gps.error) {
      let errorMessage = 'Ошибка GPS';
      switch (gps.error.code) {
        case 1: // PERMISSION_DENIED
          errorMessage = 'Геолокация заблокирована. Нажмите на замок 🔒 в адресной строке → Разрешить геолокацию, затем обновите страницу.';
          break;
        case 2: // POSITION_UNAVAILABLE
          errorMessage = 'Местоположение недоступно. Проверьте включён ли GPS.';
          break;
        case 3: // TIMEOUT
          errorMessage = 'Не удалось получить GPS за 10 сек. Попробуйте выйти на улицу.';
          break;
      }
      setState((prev) => ({ ...prev, error: errorMessage, isLoading: false }));
    }
  }, [gps.error]);

  // Auto-start on mount if requested
  useEffect(() => {
    if (autoStart) {
      start();
    }

    return () => {
      stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount/unmount

  return {
    state,
    actions: {
      start,
      stop,
      reroute,
      rerouteFromPoint,
      setCosting,
      toggleFollow,
    },
  };
}
